import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Typography,
} from '@mui/material'
import { AdminThemeDto } from '@api/types/themes'
import { themesApi } from '@api/endpoints/themes'

interface ThemeAwardDialogProps {
  open: boolean
  theme: AdminThemeDto | null
  onClose: () => void
  onComplete: () => void
}

export function ThemeAwardDialog({ open, theme, onClose, onComplete }: ThemeAwardDialogProps) {
  const [userIdsText, setUserIdsText] = useState('')
  const [mode, setMode] = useState<'award' | 'revoke'>('award')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (!theme) return
    const userIds = userIdsText
      .split(/[,\n]/)
      .map((id) => id.trim())
      .filter(Boolean)

    if (userIds.length === 0) {
      setError('Please enter at least one user ID')
      return
    }

    setLoading(true)
    setError(null)
    setResult(null)
    try {
      if (mode === 'award') {
        const res = await themesApi.awardTheme(theme.id, { userIds })
        setResult(`Awarded theme to ${res.awarded} user(s)`)
      } else {
        const res = await themesApi.revokeTheme(theme.id, { userIds })
        setResult(`Revoked theme from ${res.revoked} user(s)`)
      }
      onComplete()
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${mode} theme`)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setUserIdsText('')
    setError(null)
    setResult(null)
    setMode('award')
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Award / Revoke Theme: {theme?.name}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {result && <Alert severity="success" sx={{ mb: 2 }}>{result}</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter user IDs, separated by commas or one per line.
        </Typography>
        <TextField
          multiline
          rows={4}
          fullWidth
          value={userIdsText}
          onChange={(e) => setUserIdsText(e.target.value)}
          placeholder="user-id-1&#10;user-id-2"
          sx={{ mb: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={() => { setMode('revoke'); handleSubmit() }}
          color="warning"
          variant="outlined"
          disabled={loading || !userIdsText.trim()}
        >
          {loading && mode === 'revoke' ? 'Revoking...' : 'Revoke'}
        </Button>
        <Button
          onClick={() => { setMode('award'); handleSubmit() }}
          color="primary"
          variant="contained"
          disabled={loading || !userIdsText.trim()}
        >
          {loading && mode === 'award' ? 'Awarding...' : 'Award'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
